import { Button, pantryColors, pantryTypography } from '@pantry/ui'
import { Formik, useFormikContext } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import type {
  LoyaltyRedemptionRules,
  LoyaltyRules,
} from '../../../../types/loyalty'
import { useBsLoyaltyToggle } from '../../hooks/useBsLoyaltyToggle'
import { useUpdateLoyaltyRules } from '../../hooks/useLoyalty'
import { useLoyaltyContext } from '../../hooks/useLoyaltyContext'
import {
  LoyaltyProgramConfirmDialog,
  type LoyaltyProgramIntent,
} from '../Points/LoyaltyProgramConfirmDialog'
import { LoyaltyProgramTogglePanel } from '../Points/LoyaltyProgramTogglePanel'
import { LoadingSpinner } from '../shared/LoadingSpinner'
import { BonusPointsCard } from './rules/BonusPointsCard'
import { EarnRulesCard } from './rules/EarnRulesCard'
import { RedemptionRulesCard } from './rules/RedemptionRulesCard'
import {
  RuleCardActionsContext,
  type RuleCardPath,
  type SaveCardResult,
} from './rules/RuleCardActionsContext'
import {
  pointsPerDollarFromWireRate,
  wireRateFromPointsPerDollar,
} from './rules/redemptionRate'

const p = pantryColors
const F = pantryTypography.fontFamily

const numericString = (label: string, allowDecimal: boolean) =>
  Yup.string()
    .trim()
    .required(`${label} is required`)
    .matches(
      allowDecimal ? /^\d+(\.\d+)?$/ : /^\d+$/,
      allowDecimal
        ? `${label} must be a non-negative number`
        : `${label} must be a whole number`,
    )

const nonNegativeInt = (label: string) =>
  Yup.number()
    .typeError(`${label} must be a number`)
    .integer(`${label} must be a whole number`)
    .min(0, `${label} must be 0 or greater`)
    .required(`${label} is required`)

const rulesSchema = Yup.object({
  earn: Yup.object({
    base_rate: numericString('Base earn rate', true),
    silver: Yup.object({
      rate: numericString('Silver multiplier', true),
      min_lifetime_pts: nonNegativeInt('Silver threshold'),
    }),
    gold: Yup.object({
      rate: numericString('Gold multiplier', true),
      min_lifetime_pts: nonNegativeInt('Gold threshold'),
    }),
    platinum: Yup.object({
      rate: numericString('Platinum multiplier', true),
      min_lifetime_pts: nonNegativeInt('Platinum threshold'),
    }),
    enable: Yup.boolean().required(),
  }),
  redemption: Yup.object({
    // Displayed as points-per-dollar; must be > 0 so the reciprocal we persist
    // (dollars-per-point) is always finite.
    rate: numericString('Redemption rate', true).test(
      'positive',
      'Redemption rate must be greater than 0',
      (value) => Number(value) > 0,
    ),
    minimum_redemption: nonNegativeInt('Minimum redemption'),
    max_redemption_per_time: nonNegativeInt('Max redemption per order'),
    allow_partial_redemption: Yup.boolean().required(),
  }),
  bonus_points: Yup.object({
    signup_bonus: nonNegativeInt('Sign-up bonus'),
    birthday_bonus: nonNegativeInt('Birthday bonus'),
    referral_bonus: nonNegativeInt('Referral bonus'),
    double_points_events: Yup.boolean().required(),
    flat_points_per_order: nonNegativeInt('Points per order'),
    enable: Yup.boolean().required(),
  }),
  expiry_limits: Yup.object({
    points_expiry: nonNegativeInt('Points expiry'),
    expiry_warning: nonNegativeInt('Expiry warning'),
    enable_point_expiry: Yup.boolean().required(),
    points_on_discounted_items: Yup.boolean().required(),
  }),
})

const RulesFormBody: React.FC<{
  mutation: ReturnType<typeof useUpdateLoyaltyRules>
}> = ({ mutation }) => {
  const { values, validateForm, initialValues } =
    useFormikContext<LoyaltyRules>()

  // Last-saved server document. Seeded from the initial load and advanced one
  // card at a time as each card is saved. Non-focused cards are always sent from
  // here (their persisted state), never from their in-progress form values.
  const baselineRef = React.useRef<LoyaltyRules>(initialValues)

  const saveCard = React.useCallback(
    async (
      cardPath: RuleCardPath,
      enableOverride?: boolean,
    ): Promise<SaveCardResult> => {
      // Validate only the focused card — an invalid in-progress edit in another
      // open card must not block saving this one.
      const errors = await validateForm()
      if (errors[cardPath]) {
        return {
          ok: false,
          error: 'Please fix the highlighted fields before saving.',
        }
      }
      const currentCard = values[cardPath]
      const cardValue =
        enableOverride === undefined
          ? currentCard
          : { ...currentCard, enable: enableOverride }
      const body: LoyaltyRules = {
        ...baselineRef.current,
        [cardPath]: cardValue,
      }
      // The form holds redemption.rate as points-per-dollar; the wire stores it
      // as dollars-per-point. Every save is a full-document PUT, so convert on the
      // way out regardless of which card was edited. The baseline stays in display
      // units to keep the field and subsequent saves consistent.
      const wireBody: LoyaltyRules = {
        ...body,
        redemption: {
          ...body.redemption,
          rate: wireRateFromPointsPerDollar(body.redemption.rate),
        },
      }
      try {
        await mutation.mutateAsync(wireBody)
        baselineRef.current = body
        return { ok: true }
      } catch (error) {
        // Surface the server's reason so the client knows why the save failed,
        // falling back to a generic message when the error carries none.
        return {
          ok: false,
          error:
            error instanceof Error && error.message
              ? error.message
              : "Couldn't save your changes. Please try again.",
        }
      }
    },
    [values, validateForm, mutation],
  )

  return (
    <RuleCardActionsContext.Provider
      value={{ saveCard, isSaving: mutation.isPending }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          fontFamily: F,
        }}
      >
        <EarnRulesCard />
        <BonusPointsCard />
        <RedemptionRulesCard />
      </div>
    </RuleCardActionsContext.Provider>
  )
}

interface RulesTabProps {
  storeId: string
}

export const RulesTab: React.FC<RulesTabProps> = ({ storeId }) => {
  // Rules are fetched at the page provider (LoyaltyProvider) — the Points tab
  // and the member-drawer tier progress both consume the same cached document.
  const {
    rules: data,
    rulesLoading: isLoading,
    rulesError: isError,
    refetchRules: refetch,
  } = useLoyaltyContext()
  const mutation = useUpdateLoyaltyRules(storeId)

  // Store-level loyalty program on/off (enable_bs_loyalty). Drives the master
  // panel and gates every card below it when the program is disabled.
  const {
    enabled: programEnabled,
    isSaving: togglingProgram,
    toggle: toggleProgram,
  } = useBsLoyaltyToggle()

  // Flipping the program is confirmed via a dialog. The requested intent is
  // captured on open so the copy stays stable while the optimistic toggle and
  // save run. Cleared automatically once the save settles.
  const [pendingIntent, setPendingIntent] =
    React.useState<LoyaltyProgramIntent | null>(null)
  const wasTogglingRef = React.useRef(false)
  React.useEffect(() => {
    if (wasTogglingRef.current && !togglingProgram) {
      setPendingIntent(null)
    }
    wasTogglingRef.current = togglingProgram
  }, [togglingProgram])

  const requestToggle = (next: boolean) => {
    setPendingIntent(next ? 'enable' : 'disable')
  }
  const confirmToggle = () => {
    if (pendingIntent) toggleProgram(pendingIntent === 'enable')
  }
  const cancelToggle = () => {
    setPendingIntent(null)
  }

  // Strip the legacy `enable` flag from the redemption sub-document so it never
  // enters form state, the save baseline, or the PUT body. Redemption Rules has
  // no on/off concept; the other cards keep their `enable` flag untouched.
  const initialValues = React.useMemo(() => {
    if (!data) return data
    const { enable: _enable, ...redemption } =
      data.redemption as LoyaltyRedemptionRules & { enable?: boolean }
    // Seed the field in display units (points-per-dollar). The wire stores the
    // reciprocal (dollars-per-point); saveCard converts it back on write.
    return {
      ...data,
      redemption: {
        ...redemption,
        rate: pointsPerDollarFromWireRate(redemption.rate),
      },
    }
  }, [data])

  let body: React.ReactNode
  if (isLoading) {
    body = <LoadingSpinner label="Loading rules…" />
  } else if (isError || !data || !initialValues) {
    body = (
      <div
        style={{
          background: p.white,
          border: `1px solid ${p.gray[200]}`,
          borderRadius: 10,
          padding: '40px 24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: p.gray[700],
            marginBottom: 6,
          }}
        >
          Couldn't load loyalty rules
        </div>
        <div style={{ fontSize: 13, color: p.gray[500], marginBottom: 16 }}>
          Something went wrong while fetching the rules document.
        </div>
        <Button variant="ghost" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  } else {
    body = (
      <Formik<LoyaltyRules>
        initialValues={initialValues}
        // No enableReinitialize: saving one card refetches the rules query, and
        // a reinit would discard in-progress edits in other open cards. The form
        // is the source of truth once mounted; per-card saves keep it in sync.
        validationSchema={rulesSchema}
        // Saving is driven per-card via RuleCardActionsContext; there is no
        // global submit. This handler only satisfies Formik's required prop.
        onSubmit={() => {}}
      >
        <RulesFormBody mutation={mutation} />
      </Formik>
    )
  }

  // When the program is off, blur + dim the boxes below and block interaction.
  // The panel itself stays outside this wrapper so the toggle remains usable.
  const gated = !programEnabled

  return (
    <div style={{ display: 'grid', gap: 16, fontFamily: F }}>
      <LoyaltyProgramTogglePanel
        enabled={programEnabled}
        saving={togglingProgram}
        onChange={requestToggle}
      />
      <div
        aria-hidden={gated || undefined}
        style={{
          transition: 'opacity 120ms ease, filter 120ms ease',
          ...(gated
            ? {
                opacity: 0.5,
                pointerEvents: 'none',
                userSelect: 'none',
                filter: 'blur(1.5px) grayscale(0.4)',
              }
            : null),
        }}
      >
        {body}
      </div>
      <LoyaltyProgramConfirmDialog
        open={pendingIntent !== null}
        intent={pendingIntent ?? 'enable'}
        loading={togglingProgram}
        onConfirm={confirmToggle}
        onCancel={cancelToggle}
      />
    </div>
  )
}
